/**
 * Shared client-error noise filter — the single source of truth for which
 * client-side errors are un-actionable noise (browser/extension/third-party
 * script chatter, well-known transient network blips) and therefore must NOT
 * reach PostHog Error Tracking or page anyone.
 *
 * Consumed by BOTH:
 *  - registerGlobalHandlers() in clientErrorReporter.ts (window.onerror /
 *    unhandledrejection), and
 *  - the posthog.init `before_send` filter in posthog.ts.
 *
 * Why both: posthog-js' exception autocapture (`capture_exceptions: true`)
 * hooks window.onerror / onunhandledrejection DIRECTLY, so it bypasses the
 * app's own window handlers and the allowlist they apply. That bypass is why
 * "ResizeObserver loop…" still reached Error Tracking despite being filtered
 * here. Re-applying this predicate inside `before_send` closes the gap with a
 * single source of truth (audited 2026-06-27).
 *
 * Update sparingly — false negatives (a real bug suppressed) hurt more than
 * false positives (a little extra noise).
 */
export function shouldIgnoreError(
  message: string,
  source?: string,
  errorName?: string,
): boolean {
  // Expected API outcomes — 4xx the backend returned on purpose, thrown as
  // ExpectedClientApiError by clientApi.ts. Matched by error NAME because
  // posthog-js' before_send only sees the class name, never statusCode.
  // Plain ClientApiError (404 / 5xx) is NOT ignored.
  if (errorName === "ExpectedClientApiError") return true;
  // A deliberately cancelled operation — React Query aborting an in-flight fetch
  // on unmount/refetch, a navigation, the user hitting ✕. Reaches
  // unhandledrejection only when something outside our code (a fetch-wrapping
  // browser extension — TanStack/query#9877) holds a dangling promise; never
  // user-visible. Timeouts are `TimeoutError` and still get through.
  if (errorName === "AbortError") return true;
  if (!message) return true;
  // Browser extension noise
  if (/extension:\/\//i.test(source || "")) return true;
  if (/chrome-extension:\/\//i.test(source || "")) return true;
  if (/moz-extension:\/\//i.test(source || "")) return true;
  // Opaque cross-origin error — browsers emit the literal "Script error." with
  // no stack/detail when a third-party (or extension) script throws. Un-actionable.
  if (message === "Script error.") return true;
  // Third-party SDK noise we can't fix from app code — e.g. Stripe Elements
  // mount/ready races thrown from inside js.stripe.com / clover/stripe.js.
  if (/js\.stripe\.com|stripe\.js|\/clover\//i.test(source || "")) return true;
  // Same Stripe race arriving as an unhandledrejection, which carries no source
  // (Embedded Checkout unmounting on redirect). Matched by message for that reason.
  if (message.startsWith("We could not retrieve data from the specified Element")) return true;
  // ResizeObserver loop warnings — benign and unfixable from app code.
  if (message.includes("ResizeObserver loop")) return true;
  // Browser-extension artifact shape: posthog-js' Event coercer stringifies a
  // raw CustomEvent (typically dispatched by an extension) into this exact
  // message — no stack, no diagnostic content beyond the event's own keys.
  if (/^CustomEvent captured as exception with keys: isTrusted$/.test(message)) return true;
  // Known transient network blips that shouldn't page anyone (per-browser wording:
  // Chrome "Failed to fetch", Safari "Load failed", Firefox "Network request failed").
  if (message === "Network request failed") return true;
  if (message === "Load failed") return true;
  if (message === "Failed to fetch") return true;
  return false;
}
